// src/components/ExamCalculatorPanel.jsx
// A basic four-function calculator with memory and square root/percent,
// matching the on-screen calculator provided in the real ACCA CBE exam
// software (not a full trig/log scientific calculator - despite the
// physical-calculator-style button layout, the actual functions are basic).
// Click-only by design: no keyboard listener, so it can't steal keystrokes
// meant for the word processor/spreadsheet answer panels open alongside it.

import React, { useState } from 'react';

const MAX_DIGITS = 12;

function formatNumber(n) {
  if (!isFinite(n)) return 'Error';
  if (Number.isInteger(n) && Math.abs(n) < 10 ** MAX_DIGITS) return String(n);
  let s = n.toPrecision(MAX_DIGITS);
  if (s.includes('.')) s = s.replace(/0+$/, '').replace(/\.$/, '');
  return s;
}

function applyOperator(a, b, op) {
  switch (op) {
    case '+': return a + b;
    case '-': return a - b;
    case '×': return a * b;
    case '÷': return b === 0 ? NaN : a / b;
    default: return b;
  }
}

const CalcButton = ({ label, onClick, variant = 'default', span }) => {
  const palette = {
    default: { background: 'white', color: '#2c3e50', border: '1px solid #e2e8f0' },
    op: { background: '#eef1ff', color: '#4c51bf', border: '1px solid #c3c9f7' },
    memory: { background: '#f8f9fa', color: '#718096', border: '1px solid #e2e8f0', fontSize: '11px' },
    equals: { background: '#667eea', color: 'white', border: '1px solid #667eea' },
    clear: { background: '#fff5f5', color: '#e53e3e', border: '1px solid #fed7d7' }
  };
  const style = palette[variant] || palette.default;
  return (
    <button
      type="button"
      onClick={onClick}
      style={{
        ...style,
        gridColumn: span ? `span ${span}` : undefined,
        padding: '10px 0',
        borderRadius: '6px',
        cursor: 'pointer',
        fontWeight: '700',
        fontSize: style.fontSize || '15px',
        fontFamily: 'inherit'
      }}
    >
      {label}
    </button>
  );
};

const ExamCalculatorPanel = () => {
  const [display, setDisplay] = useState('0');
  const [storedValue, setStoredValue] = useState(null);
  const [pendingOp, setPendingOp] = useState(null);
  const [waitingForOperand, setWaitingForOperand] = useState(false);
  const [memory, setMemory] = useState(0);

  const currentValue = parseFloat(display);

  const inputDigit = (digit) => {
    if (waitingForOperand) {
      setDisplay(digit);
      setWaitingForOperand(false);
      return;
    }
    if (display === '0') {
      setDisplay(digit);
    } else if (display.replace('-', '').replace('.', '').length < MAX_DIGITS) {
      setDisplay(display + digit);
    }
  };

  const inputDecimal = () => {
    if (waitingForOperand) {
      setDisplay('0.');
      setWaitingForOperand(false);
      return;
    }
    if (!display.includes('.')) setDisplay(display + '.');
  };

  const clearAll = () => {
    setDisplay('0');
    setStoredValue(null);
    setPendingOp(null);
    setWaitingForOperand(false);
  };

  const clearEntry = () => setDisplay('0');

  const backspace = () => {
    if (waitingForOperand) return;
    const next = display.slice(0, -1);
    setDisplay(next === '' || next === '-' ? '0' : next);
  };

  const toggleSign = () => {
    if (display === '0') return;
    setDisplay(display.startsWith('-') ? display.slice(1) : '-' + display);
  };

  const squareRoot = () => {
    setDisplay(formatNumber(Math.sqrt(currentValue)));
    setWaitingForOperand(true);
  };

  const percent = () => {
    if (pendingOp && storedValue !== null) {
      setDisplay(formatNumber((storedValue * currentValue) / 100));
    } else {
      setDisplay(formatNumber(currentValue / 100));
    }
    setWaitingForOperand(true);
  };

  const chooseOperator = (op) => {
    if (pendingOp && !waitingForOperand) {
      const result = applyOperator(storedValue, currentValue, pendingOp);
      setStoredValue(result);
      setDisplay(formatNumber(result));
    } else {
      setStoredValue(currentValue);
    }
    setPendingOp(op);
    setWaitingForOperand(true);
  };

  const equals = () => {
    if (pendingOp === null || storedValue === null) return;
    const result = applyOperator(storedValue, currentValue, pendingOp);
    setDisplay(formatNumber(result));
    setStoredValue(null);
    setPendingOp(null);
    setWaitingForOperand(true);
  };

  const memoryClear = () => setMemory(0);
  const memoryRecall = () => { setDisplay(formatNumber(memory)); setWaitingForOperand(true); };
  const memoryAdd = () => setMemory(memory + currentValue);
  const memorySubtract = () => setMemory(memory - currentValue);
  const memoryStore = () => setMemory(currentValue);

  return (
    <div style={{ padding: '12px', background: '#f8f9fa', height: '100%', boxSizing: 'border-box', display: 'flex', flexDirection: 'column', gap: '10px' }}>
      <div style={{
        background: '#1a2332', color: 'white', borderRadius: '6px', padding: '14px 12px',
        textAlign: 'right', fontSize: '26px', fontWeight: '600', fontVariantNumeric: 'tabular-nums',
        overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap'
      }}>
        {display}
      </div>
      {memory !== 0 && (
        <div style={{ fontSize: '11px', color: '#718096', textAlign: 'right', marginTop: '-6px' }}>
          M = {formatNumber(memory)}
        </div>
      )}

      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(5, 1fr)', gap: '6px' }}>
        <CalcButton label="MC" variant="memory" onClick={memoryClear} />
        <CalcButton label="MR" variant="memory" onClick={memoryRecall} />
        <CalcButton label="M+" variant="memory" onClick={memoryAdd} />
        <CalcButton label="M-" variant="memory" onClick={memorySubtract} />
        <CalcButton label="MS" variant="memory" onClick={memoryStore} />
      </div>

      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: '6px', flex: 1 }}>
        <CalcButton label="C" variant="clear" onClick={clearAll} />
        <CalcButton label="CE" variant="clear" onClick={clearEntry} />
        <CalcButton label="⌫" onClick={backspace} />
        <CalcButton label="√" variant="op" onClick={squareRoot} />

        <CalcButton label="7" onClick={() => inputDigit('7')} />
        <CalcButton label="8" onClick={() => inputDigit('8')} />
        <CalcButton label="9" onClick={() => inputDigit('9')} />
        <CalcButton label="÷" variant="op" onClick={() => chooseOperator('÷')} />

        <CalcButton label="4" onClick={() => inputDigit('4')} />
        <CalcButton label="5" onClick={() => inputDigit('5')} />
        <CalcButton label="6" onClick={() => inputDigit('6')} />
        <CalcButton label="×" variant="op" onClick={() => chooseOperator('×')} />

        <CalcButton label="1" onClick={() => inputDigit('1')} />
        <CalcButton label="2" onClick={() => inputDigit('2')} />
        <CalcButton label="3" onClick={() => inputDigit('3')} />
        <CalcButton label="−" variant="op" onClick={() => chooseOperator('-')} />

        <CalcButton label="%" variant="op" onClick={percent} />
        <CalcButton label="0" onClick={() => inputDigit('0')} />
        <CalcButton label="." onClick={inputDecimal} />
        <CalcButton label="+" variant="op" onClick={() => chooseOperator('+')} />

        <CalcButton label="+/-" onClick={toggleSign} span={2} />
        <CalcButton label="=" variant="equals" onClick={equals} span={2} />
      </div>
    </div>
  );
};

export default ExamCalculatorPanel;
